import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import api from "@/lib/api";
import { displayNzTimestamp } from "@/lib/dates";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "./_shared";
import { TopicContent, sessionTopics } from "@/components/toolbox/TopicContent";
import { SignOnDialog } from "@/components/toolbox/SignOnDialog";
import {
  ArrowLeft, Play, PenLine, Users, CheckCircle2, Circle, FileDown,
  MapPin, Calendar, Clock, User, AlertTriangle, Shield, HardHat, Loader2, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

const DELIVER = ["admin", "office", "super_admin", "foreman", "quantity_surveyor", "field"];

function Section({ icon: Icon, label, children, accent = "#FF5F15" }) {
  return (
    <div className="hard-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: accent }}>{label}</div>
      </div>
      {children}
    </div>
  );
}

export default function ToolboxDeliver() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [talk, setTalk] = useState(null);
  const [status, setStatus] = useState({ expected: [], attendance: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [topicIndex, setTopicIndex] = useState(0);
  const [signMode, setSignMode] = useState(null); // "self" | "kiosk" | null

  const canDeliver = DELIVER.includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get(`/tbx/talks/${id}`),
        api.get(`/tbx/talks/${id}/sign-status`),
      ]);
      setTalk(t.data);
      setStatus(s.data);
    } catch {
      toast.error("Could not load talk");
      nav("/app/toolbox");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try { await api.post(`/tbx/talks/${id}/start`); await load(); toast.success("Talk started"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    if (!window.confirm("Complete this talk and generate the signed PDF record? It will be filed to the job.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tbx/talks/${id}/complete`);
      toast.success(`Completed — ${data.signed} sign-on${data.signed === 1 ? "" : "s"} recorded`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openPdf = async () => {
    try {
      const res = await api.get(`/tbx/talks/${id}/pdf`, { responseType: "blob", __flSkipReadCache: true });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open PDF"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>;
  }
  if (!talk) return null;

  const topics = sessionTopics(talk);
  const activeIndex = Math.min(topicIndex, topics.length - 1);
  const attendance = status.attendance || [];
  const expected = status.expected || [];
  const isCompleted = talk.status === "completed";
  const myAttendance = attendance.find((a) => a.user_id === user?.id);

  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 max-w-4xl mx-auto pb-28 md:pb-10" data-testid="toolbox-deliver-page">
      <button onClick={() => nav("/app/toolbox")} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#FF5F15] mb-4" data-testid="deliver-back">
        <ArrowLeft className="w-4 h-4" /> Back to Toolbox Talks
      </button>

      {/* Header */}
      <div className="hard-card p-5 md:p-6 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-1">// Toolbox Talk</div>
            <h1 className="text-2xl md:text-3xl font-display uppercase tracking-tight text-zinc-900 dark:text-white">{talk.topic_title_snapshot}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-zinc-500 dark:text-zinc-400">
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {talk.job_number} — {talk.job_title}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {talk.talk_date}</span>
              {talk.start_time && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {talk.start_time}</span>}
              <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {talk.delivered_by_name}</span>
            </div>
          </div>
          <StatusBadge status={talk.status} />
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {canDeliver && (talk.status === "draft" || talk.status === "scheduled") && (
            <Button onClick={start} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="deliver-start">
              <Play className="w-4 h-4 mr-1.5" /> Start talk
            </Button>
          )}
          {!isCompleted && (
            <>
              <Button onClick={() => setSignMode("self")} variant={myAttendance ? "outline" : "default"}
                className={`rounded-sm uppercase text-xs font-bold tracking-wider ${myAttendance ? "" : "bg-[#FF5F15] hover:bg-[#E65100]"}`} data-testid="deliver-sign-self">
                <PenLine className="w-4 h-4 mr-1.5" /> {myAttendance ? "Re-sign" : "Sign on"}
              </Button>
              {canDeliver && (
                <Button onClick={() => setSignMode("kiosk")} variant="outline" className="rounded-sm uppercase text-xs font-bold tracking-wider" data-testid="deliver-sign-kiosk">
                  <Users className="w-4 h-4 mr-1.5" /> Crew sign-on
                </Button>
              )}
            </>
          )}
          {canDeliver && !isCompleted && (
            <Button onClick={complete} disabled={busy} variant="outline" className="rounded-sm uppercase text-xs font-bold tracking-wider border-green-500/50 text-green-600 dark:text-green-400 hover:bg-green-500/10" data-testid="deliver-complete">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete & generate PDF
            </Button>
          )}
          {isCompleted && (
            <Button onClick={openPdf} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="deliver-pdf">
              <FileDown className="w-4 h-4 mr-1.5" /> View / download PDF
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Deliver content */}
        <div className="md:col-span-2 space-y-4">
          <Section icon={ClipboardCheck} label={`${topics.length} topic${topics.length === 1 ? "" : "s"} covered · one session`}>
            <ol className="space-y-1">{topics.map((topic, index) => <li key={index}><button onClick={() => { setTopicIndex(index); document.getElementById(`session-topic-${isCompleted ? index : "active"}`)?.scrollIntoView({ block: "start", behavior: "smooth" }); }} className={`w-full text-left py-3 text-sm ${activeIndex === index ? "text-[#FF5F15] font-bold" : ""}`}>{index + 1}. {topic.title_snapshot}</button></li>)}</ol>
          </Section>
          {(isCompleted ? topics : [topics[activeIndex]]).map((topic, index) => <div id={`session-topic-${isCompleted ? index : "active"}`} key={isCompleted ? index : activeIndex} className="hard-card p-4 md:p-5 space-y-4 scroll-mt-4">
            <div><p className="text-[10px] font-bold uppercase tracking-wide text-[#FF5F15]">Topic {(isCompleted ? index : activeIndex) + 1} of {topics.length}</p><h2 className="text-xl font-display font-bold mt-1">{topic.title_snapshot}</h2><p className="text-xs text-zinc-500">{topic.category_snapshot}</p></div>
            <TopicContent content={topic.content_snapshot} attachments={topic.attachments} />
            {!isCompleted && topics.length > 1 && <nav className="flex justify-between gap-3 pt-3 border-t"><Button variant="outline" disabled={activeIndex === 0} onClick={() => setTopicIndex(activeIndex - 1)}>Previous topic</Button><Button variant="outline" disabled={activeIndex === topics.length - 1} onClick={() => { setTopicIndex(activeIndex + 1); document.getElementById("session-topic-active")?.scrollIntoView({ block: "start" }); }}>Next topic</Button></nav>}
          </div>)}
          {talk.work_activity && (
            <Section icon={HardHat} label="Today's work">
              <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">{talk.work_activity}</p>
            </Section>
          )}
          {talk.site_specific_hazards && (
            <Section icon={AlertTriangle} label="Site-specific hazards" accent="#EF4444">
              <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">{talk.site_specific_hazards}</p>
            </Section>
          )}
          {talk.site_specific_controls && (
            <Section icon={Shield} label="Controls discussed" accent="#22C55E">
              <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">{talk.site_specific_controls}</p>
            </Section>
          )}
          {talk.notes && <Section icon={ClipboardCheck} label="Discussion / site notes"><p className="text-sm whitespace-pre-wrap">{talk.notes}</p></Section>}

        </div>

        {/* Sign-on tracker */}
        <div className="space-y-4">
          <div className="hard-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold">Sign-on</div>
              <span className="text-sm font-bold" data-testid="deliver-signed-count">{attendance.length} signed</span>
            </div>
            {expected.length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">Expected crew</div>
                <ul className="space-y-1.5 mb-3">
                  {expected.map((e) => (
                    <li key={e.id} className="flex items-center gap-2 text-sm" data-testid={`deliver-expected-${e.id}`}>
                      {e.signed ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Circle className="w-4 h-4 text-zinc-300 dark:text-zinc-600" />}
                      <span className={e.signed ? "text-zinc-900 dark:text-white" : "text-zinc-500"}>{e.name}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">Signed on</div>
            {attendance.length === 0 ? (
              <p className="text-sm text-zinc-500">No sign-ons yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {attendance.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`deliver-attendee-${a.id}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      <span className="truncate">{a.worker_name}</span>
                      {a.attendance_type !== "EMPLOYEE" && <Badge variant="outline" className="text-[9px] uppercase">{a.attendance_type}</Badge>}
                    </span>
                    <span className="text-[11px] text-zinc-400 shrink-0">{displayNzTimestamp(a.signed_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <SignOnDialog
        open={!!signMode}
        onOpenChange={(v) => !v && setSignMode(null)}
        talkId={id}
        topics={topics}
        topicsRevision={talk.topics_revision}
        expected={expected}
        currentUser={user}
        mode={signMode || "self"}
        onSigned={load}
      />
    </div>
  );
}
